import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as actions from '../store/actions';
import * as fromStore from '../store/reducers';
import { ActionError } from '../../pillar/common/actions/ActionError';
import { DataSetRootEntityModel } from '../models/DataSetRootEntityModel';
import { ModuleConfiguration } from '../datawarehouse.module.config';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { HttpErrorResponse } from '@angular/common/http';
import { QueryResponse } from '../../core/services/QueryResponse';
import { DataWarehouseService } from '../../core/services';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ModelBase } from '../../pillar/common/models/ModelBase';
import { LoadEntityListPayload } from '../models/LoadEntityListPayload';
import { ListMetadataModel } from '../models/ListMetadataModel';
import { DataWarehouseWorkspaceModelResponse } from '../models/DataWarehouseWorkspaceModelResponse';
import { SaveResultsModel } from '../models/SaveResultsModel';
import { NextgateSyncModel } from '../models/NextgateSyncModel';


@Injectable()
export class DataWarehouseServiceEffects {
  private _submittingEntityKey: string;

  constructor(private actions$: Actions,
    private dataWarehouseService: DataWarehouseService,
    private store: Store<any>) {
    store.select(fromStore.getSubmittingEntity)
      .subscribe(s => {
        if (s) {
          this._submittingEntityKey = s.key;
        } else {
          this._submittingEntityKey = null;
        }
        this.dataWarehouseService.currentSubmittingEntityKey = this._submittingEntityKey;
      });
  }


  @Effect()
  getDynamicDataSetList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<DataSetRootEntityModel>(ModuleConfiguration.moduleName, DataSetRootEntityModel))
    .map((a: dashboardActions.QueryListAction<DataSetRootEntityModel>) => a.payload)
    .switchMap((payload: LoadPagePayload) => this.dataWarehouseService.getDataSetRootEntities(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DataSetRootEntityModel>(error)))
      .map((response: QueryResponse<DataSetRootEntityModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, DataSetRootEntityModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, DataSetRootEntityModel, { items: response.items, totalCount: response.totalCount });
      }));

  @Effect()
  getEntityList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<any>(ModuleConfiguration.moduleName, ModelBase))
    .map((a: dashboardActions.QueryListAction<any>) => a.payload)
    .flatMap((payload: LoadEntityListPayload) => this.dataWarehouseService.getWarehouseList(payload.entityKey, payload.parentEntityKey, payload.parentKey, payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm, this._submittingEntityKey, payload.showDeleted)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<any>(error)))
      .map((response: QueryResponse<any>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, ModelBase, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, ModelBase, {
          entityKey: payload.entityKey,
          scope: payload.scope,
          items: response.items, totalCount: response.totalCount
        });
      }));

  @Effect()
  getEntityListMetadata$: Observable<Action> = this.actions$.ofType(actions.GET_ENTITY_LISTMETADATA)
    .map((a: actions.GetEntityListMetadataAction) => a.payload)
    .switchMap((payload) => this.dataWarehouseService.getWarehouseListMetadata(payload.entityKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<any>(error)))
      .map((response: ModelResponse<ListMetadataModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, ModelBase, { errors: response.messages.map(m => m.value) });
        }
        return new actions.GetEntityListMetadataSuccessAction({ ...payload, metadata: response.model });
      }));

  @Effect()
  getWarehouseWorkspace$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse))
    .switchMap((a: dashboardActions.GetSelectionAction<DataWarehouseWorkspaceModelResponse>) => this.dataWarehouseService.getWarehouseWorkspace(a.payload.entityKey, a.payload.key, this._submittingEntityKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<any>(error)))
      .map((response: DataWarehouseWorkspaceModelResponse) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse, { errors: response.messages.map(m => m.value) });
        }
        var warehouseActionPayload = a.payload as any;
        return new dashboardActions.GetSelectionSuccessAction(
          ModuleConfiguration.moduleName,
          DataWarehouseWorkspaceModelResponse,
          {
            entityKey: warehouseActionPayload.entityKey,
            scope: warehouseActionPayload.scope,
            model: response
          });
      }));

  @Effect()
  reviseRootEntity$: Observable<Action> = this.actions$.ofType(actions.REVISE_ROOT_ENTITY)
    .switchMap((a: actions.ReviseRootEntityAction) => this.dataWarehouseService.reviseRootEntity(a.payload.entityKey, a.payload.key, a.payload.model, this._submittingEntityKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<SaveResultsModel>(error)))
      .map((response: ModelResponse<SaveResultsModel>) => {
        return new actions.ReviseRootEntityResponseAction(
          {
            entityKey: a.payload.entityKey,
            key: a.payload.key,
            scope: a.payload.scope,
            model: a.payload.model,
            results: response.model
          });
      }));

  @Effect()
  addWarehouseWorkspace$: Observable<Action> = this.actions$.ofType(actions.ADD_WORKSPACE)
    .switchMap((a: actions.AddWorkspaceAction) => this.dataWarehouseService.addWarehouseWorkspace(a.payload.entityKey, a.payload.parentEntityKey, a.payload.parentKey, this._submittingEntityKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<any>(error)))
      .map((response: DataWarehouseWorkspaceModelResponse) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse, { errors: response.messages.map(m => m.value) });
        }
        var warehouseActionPayload = a.payload as any;
        return new dashboardActions.GetSelectionSuccessAction(
          ModuleConfiguration.moduleName,
          DataWarehouseWorkspaceModelResponse,
          {
            entityKey: warehouseActionPayload.entityKey,
            scope: warehouseActionPayload.scope,
            model: response
          });
      }));

  @Effect()
  syncWithNextgate$: Observable<Action> = this.actions$.ofType(actions.SYNC_WITH_NEXTGATE)
    .switchMap((a: actions.SyncWithNextgateAction) => this.dataWarehouseService.syncWithNextgate(a.payload.key, a.payload.isNew, a.payload.currentAction)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<any>(error)))
      .map((response: ModelResponse<NextgateSyncModel>) => {
        return new actions.SyncWithNextgateResponseAction(
          {
            key: a.payload.key,
            result: response.model
          }
        );
      }));

  @Effect()
  cloneWarehouseWorkspace$: Observable<Action> = this.actions$.ofType(actions.CLONE_WORKSPACE)
    .switchMap((a: actions.CloneWorkspaceAction) => this.dataWarehouseService.getWarehouseWorkspace(a.payload.entityKey, a.payload.key, this._submittingEntityKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<any>(error)))
      .map((response: DataWarehouseWorkspaceModelResponse) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, DataWarehouseWorkspaceModelResponse, { errors: response.messages.map(m => m.value) });
        }

        this.nullOutKeys(response.model);
        response.isClone = true;

        var warehouseActionPayload = a.payload as any;
        return new dashboardActions.GetSelectionSuccessAction(
          ModuleConfiguration.moduleName,
          DataWarehouseWorkspaceModelResponse,
          {
            entityKey: warehouseActionPayload.entityKey,
            scope: warehouseActionPayload.scope,
            model: response
          });
      }));

    nullOutKeys(model: any){
      for(var property in model){
        if(property == 'key' && model[property].length == 36){
          model[property] = null;
        }
        else {
          var service = this;        
          if(Array.isArray(model[property])){
            model[property].forEach(function(item){
              service.nullOutKeys(item);
            });          
          } else if(typeof model[property] === 'object' && model[property] !== null){
            service.nullOutKeys(model[property]);
          }
        }
      }
    }
}



// WEBPACK FOOTER //
// ./src/app/datawarehouse/service/DataWarehouseService.effects.ts